<script setup>
    import Swal from 'sweetalert2';
    import Layout from "@/layouts/main.vue";
    import PageHeader from "@/components/page-header";  
    import "@suadelabs/vue3-multiselect/dist/vue3-multiselect.css";
    import { ref, onMounted, computed } from 'vue';
    import axios from 'axios';
    import { useRoute } from 'vue-router';
    import { DataTable, TableBody, TableHead, TableBodyCell } from '@jobinsjp/vue3-datatable';
    import '@jobinsjp/vue3-datatable/dist/style.css';
    import moment from 'moment';
    import { authHeader } from './../../../../helpers/authservice/auth-header';
    import store from '../../../../state/store';

    const route = useRoute();

    const loading = computed(() => store.state.globals.loading);
    const title = ref("View LGU");
    const items = ref([
        {
            text: "Home",
            href: "/",
        },
        {
            text: "Settings",
            href: "/settings/lgu/list",
            active: true
        },
        {
            text: "View LGU",
            active: true,
        }
    ]);

    const lgu = ref({
        id: '',
        name: '',
        code: null,
        token: null,
        no_contact_link: '',
        order_payment_api: '',
        skip_ovr: false,
        address: '',
        email: '',
        contact: '',
        basic_fee: '',
        abbreviation: '',
        description: '',
        remarks: '',
        enable_multiple_violation: true
    });

    const getLGUDetails = async () => {
        store.dispatch('globals/loading', null, { root: true });

        try {
          const { data } = await axios.get(`/lgu/${route.params.id}`, {
            headers: authHeader()
          });
          lgu.value = data.data;
          store.dispatch('globals/loaded', null, { root: true });
        } catch (err) {
          console.log(err);
          store.dispatch('globals/loaded', null, { root: true });
        }
    }

    const confirmDeletion = async () => {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Delete",
      }).then((result) => {
        if (result.value) {
          store.dispatch('lgu/deleteLGU', lgu.value.id);
          Swal.fire("Deleted!", `LGU ${lgu.value.name} has been successfully deleted!`, "success");
        }
      });
    }

    onMounted(async () => {
        await getLGUDetails();
    });
</script>

<template>
  <Layout>
    <PageHeader back="/settings/lgu/list" :title="title" :items="items" />
    <div class="row">
      <div class="col-xxl-3">
        <b-overlay :show="loading">
          <div class="card">
            <div class="card-body p-4">
              <div>
                <div class="d-flex justify-content-between">
                  <router-link :to="`/settings/lgu/${lgu.id}/edit`">
                      <i class="mdi mdi-pencil"></i>
                      Edit LGU
                  </router-link>
                </div>
                <div class="mt-4 text-center">
                  <h5 class="mb-4">{{ lgu.name }}</h5>
                </div>
                <div class="table-responsive mb-4">
                  <table class="table mb-0 table-borderless">
                    <tbody>
                      <tr>
                        <th><span class="fw-medium">Contact</span></th>
                        <td>{{ lgu.contact }}</td>
                      </tr>
                      <tr>
                        <th><span class="fw-medium">Email</span></th>
                        <td>{{ lgu.email }}</td>
                      </tr>
                      <tr>
                        <th><span class="fw-medium">Address</span></th>
                        <td>{{ lgu.address }}</td>
                      </tr>
                      <tr>
                        <th><span class="fw-medium">No Contact Link</span></th>
                        <td>
                          <a :href="lgu.no_contact_link" class="link-primary"
                            >{{ lgu.no_contact_link }}</a
                          >
                        </td>
                      </tr>
                      <tr>
                        <th><span class="fw-medium">Order Payment API</span></th>
                        <td>{{ lgu.order_payment_api }}</td>
                      </tr>
                      <tr>
                        <th><span class="fw-medium">LGU Code</span></th>
                        <td>{{ lgu.code }}</td>
                      </tr>
                      <tr>
                        <th><span class="fw-medium">LGU Token</span></th>
                        <td>{{ lgu.token }}</td>
                      </tr>
                      <tr>
                        <th><span class="fw-medium">Basic Fee</span></th>
                        <td>₱ {{ parseFloat(lgu.basic_fee).toFixed(2) }}</td>
                      </tr>
                      <tr>
                        <th><span class="fw-medium">Abbreviation</span></th>
                        <td>{{ lgu.abbreviation }}</td>
                      </tr>
                      <tr>
                        <th><span class="fw-medium">OVR Skipped</span></th>
                        <td>{{ lgu.skip_ovr ? 'Yes' : 'No' }}</td>
                      </tr>
                      <tr>
                        <th><span class="fw-medium">Multiple Violation Enabled</span></th>
                        <td>{{ lgu.enable_multiple_violation ? 'Yes' : 'No' }}</td>
                      </tr>
                      <tr>
                        <th><span class="fw-medium">Description</span></th>
                        <td>{{ lgu.description }}</td>
                      </tr>
                      <tr>
                        <th><span class="fw-medium">Remarks</span></th>
                        <td>{{ lgu.remarks }}</td>
                      </tr>
                      <tr>
                        <th><span class="fw-medium">Date Created</span></th>
                        <td>{{ moment(lgu.created_at).format('YYYY-MM-DD hh:mm A') }}</td>
                      </tr>
                      <tr>
                        <th><span class="fw-medium">Date Modified</span></th>
                        <td>{{ moment(lgu.updated_at).format('YYYY-MM-DD hh:mm A') }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="d-flex justify-content-between">
                  <button @click.prevent="confirmDeletion" class="btn btn-danger">
                      <i class="mdi mdi-delete mr-1"></i>
                      Delete LGU
                  </button>
                </div>
              </div>
            </div>
            <!--end card-body-->
          </div>
        </b-overlay>
        <!--end card-->
      </div>
      <!--end col-->

      <div class="col-xxl-9">
        <div class="card">
          <div class="card-header border-0 align-items-center d-flex">
            <h4 class="card-title mb-0 flex-grow-1">Camera Lists <b-badge variant="success" class="ml-3">Active Camera: 0</b-badge></h4>
          </div>
          <!-- end card header -->

          <div class="card-body p-0 pb-2">
              <div class="row">
                <div class="col lg-12">
                    <data-table :rows="[]"
                        striped
                        filter
                        hoverable
                        sortable
                        @loadData="[]"
                    >
                    <template #thead>
                        <table-head>Camera ID</table-head>
                        <table-head>Location</table-head>
                        <table-head>Installed Date</table-head>
                        <table-head>Status</table-head>
                    </template>

                    <template #tbody>
                        <table-body>Camera ID</table-body>
                        <table-body>Location</table-body>
                        <table-body>Installed Date</table-body>
                        <table-body>Status</table-body>
                    </template>

                    <template #empty>
                        <TableBodyCell colspan="4">
                            No camera found in the list.
                        </TableBodyCell>
                    </template>
                </data-table>
                </div>
            </div>
          </div>
          <!-- end card body -->
        </div>
        <!-- end card -->
      </div>
      <!--end col-->
    </div>
  </Layout>
</template>